import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FoundationIcon from '@mui/icons-material/Foundation';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import PetsIcon from '@mui/icons-material/Pets';
import ShieldIcon from '@mui/icons-material/Shield';
import EcoIcon from '@mui/icons-material/Foundation';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';

interface Metric {
  label: string;
  value: string | number;
  status: string;
}

interface Pillar {
  title: string;
  subtitle: string;
  color: string;
  metrics: Metric[];
}

interface ExSituData {
  summary?: {
    habitat_health_index?: { value: number; status: string };
    mean_species_abundance?: { value: number; status: string };
    human_disturbance_index?: { value: number; status: string };
  };
  pillars: Pillar[];
}

interface ExSituMetricsProps {
  data?: ExSituData;
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'very high':
    case 'high':
    case 'good':
      return '#4caf50';
    case 'moderate':
    case 'medium':
      return '#ff9800';
    case 'bad':
    case 'low':
      return '#f44336';
    case 'very low':
      return '#f44336';
    default:
      return '#666666';
  }
};

const METRIC_DESCRIPTIONS: Record<string, string> = {
  "Ecosystem Integrity Index (EII)": "Measures the overall ecological intactness and functional condition of an ecosystem relative to its natural state.",
  "Native Vegetation Cover (%)": "Represents the proportion of land covered by naturally occurring native vegetation.",
  "Landscape Fragmentation Index (LFI)": "Quantifies the degree to which habitats are broken into smaller isolated patches.",
  "Animal Habitat Connectivity (CPLAND)": "Measures how well habitat patches are physically connected for wildlife movement.",
  "Habitat Health Index (HHI)": "Indicates the combined ecological condition and stability of habitat systems.",
  "Vegetation Health Index (EVI)": "Evaluates vegetation vigor, greenness, and photosynthetic activity using satellite data.",
  "Leaf Area Index (LAI)": "Measures the total leaf surface area relative to ground area, indicating vegetation density.",
  "Plant Species Richness": "Counts the total number of plant species present within a site.",
  "Plant Species Diversity (Shannon H')": "Measures both the abundance and distribution of plant species diversity.",
  "Plant Functional Diversity": "Assesses the diversity of ecological functions performed by plant species within an ecosystem.",
  "Plant Phenological Stability Index": "Evaluates the seasonal consistency and stability of vegetation growth patterns over time.",
  "Threatened Plant Species Richness": "Counts the number of threatened or vulnerable plant species present in the area.",
  "Habitat Suitability for Threatened Flora": "Indicates how suitable the habitat is for sustaining threatened plant species.",
  "Animal Species Richness": "Represents the total number of animal species occurring within a site.",
  "Animal Species Diversity (Shannon H')": "Measures the diversity and evenness of animal species distribution.",
  "Faunal Community Dissimilarity": "Quantifies ecological differences in animal community composition between sites or habitats.",
  "Threatened Animal Species Richness": "Counts the number of threatened or endangered animal species in the area.",
  "Keystone / Flagship Habitat Viability": "Evaluates the habitat’s ability to support ecologically important or flagship species.",
  "Small-Ranged / Endemic Species Richness": "Measures the number of species restricted to limited geographic ranges.",
  "Animal Habitat Suitability Index": "Assesses how favorable environmental conditions are for wildlife habitation.",
  "Mean Species Abundance (MSA)": "Indicates the average abundance of native species relative to undisturbed ecosystems.",
  "Biodiversity Intactness Index (BII)": "Measures how much original biodiversity remains compared to a pristine baseline condition.",
  "Composite Extinction Risk Index (CERI)": "Represents the cumulative extinction risk of species within the ecosystem.",
  "Potentially Disappeared Fraction (PDF)": "Estimates the fraction of species potentially lost due to environmental disturbance or land-use change.",
  "STAR_T (Threat Abatement Score)": "Measures the potential contribution of conservation actions toward reducing species extinction threats.",
  "STAR_R (Recovery Score)": "Estimates the potential biodiversity gains achievable through ecosystem restoration and recovery actions.",
  "Habitat Loss / Land Cover Change Rate": "Measures the rate at which natural habitats are converted or degraded over time.",
  "Human Disturbance Index (HDI)": "Quantifies the intensity of anthropogenic pressure and disturbance on ecosystems.",
  "Overall Biodiversity Score": "Represents an integrated assessment of overall ecological and biodiversity condition across the site."
};

const SpectrumCard = ({ label, value, status, icon: Icon }: { label: string; value: number; status: string; icon: React.ElementType }) => (
  <Grid size={{ xs: 12, md: 4 }} sx={{ p: 1 }}>
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        p: 2,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: "48px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            backgroundColor: "#2e7d32", // themed green
            borderRadius: "50%"
          }}
        >
          <Icon sx={{ fontSize: '26px' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#555' }}>{label}</Typography>
            <Tooltip title={METRIC_DESCRIPTIONS[label] || label} arrow>
              <InfoOutlinedIcon sx={{ fontSize: '16px', color: '#888' }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '24px', color: getStatusColor(status) }}>
              {value.toFixed(2)}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: getStatusColor(status) }}>
              {status}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3, px: 1 }}>
        <Box sx={{ position: 'relative', height: '6px', width: '100%', mb: 1 }}>
          {/* Spectrum Bar */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '3px',
              background: 'linear-gradient(to right, #f44336 0%, #f44336 33%, #ff9800 33%, #ff9800 66%, #4caf50 66%, #4caf50 100%)',
            }}
          />
          {/* Triangle Marker */}
          <Box
            sx={{
              position: 'absolute',
              left: `${value * 100}%`,
              top: '-12px',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: '9px solid #333',
            }}
          />
          {/* Range Labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography sx={{ fontSize: '11px', color: '#888' }}>0</Typography>
            <Typography sx={{ fontSize: '11px', color: '#888' }}>1</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Grid>
);

const MetricItem = ({ metric }: { metric: Metric }) => (
  <Box>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
      <Typography
        sx={{
          color: "#666666",
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: 1.2
        }}
      >
        {metric.label}
        <Tooltip title={METRIC_DESCRIPTIONS[metric.label] || metric.label} arrow placement="top">
          <IconButton size="small" sx={{ padding: '0 0 0 4px', color: "#666666", verticalAlign: 'middle' }}>
            <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: '16px' }} />
          </IconButton>
        </Tooltip>
      </Typography>
    </Box>
    <Box>
      <Typography
        sx={{
          color: getStatusColor(metric.status),
          fontWeight: 500,
          fontSize: '20px',
          mt: 0.5
        }}
      >
        {metric.value}
      </Typography>
    </Box>
  </Box>
);

const PillarCard = ({ pillar, icon: Icon }: { pillar: Pillar; icon: React.ElementType }) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 1 }}>
    <Box
      sx={{
        height: '100%',
        backgroundColor: "#fff",
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: pillar.color,
            backgroundColor: `${pillar.color}15`,
            borderRadius: "50%"
          }}
        >
          <Icon sx={{ fontSize: '22px' }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "17px", color: "#151E15" }}>
          {pillar.title}
        </Typography>
      </Box>
      <Box sx={{ borderTop: '1px solid #eee', mx: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 2.5 }}>
        {pillar.metrics.map((metric, index) => (
          <MetricItem key={index} metric={metric} />
        ))}
      </Box>
    </Box>
  </Grid>
);

const ExSituMetrics: React.FC<ExSituMetricsProps> = ({ data }) => {
  if (!data || !data.pillars) return null;

  const summary = data.summary || {};
  const pillarIcons = [FoundationIcon, NaturePeopleIcon, PetsIcon, ShieldIcon];

  return (
    <Box sx={{ flexGrow: 1, my: 2 }}>
      {/* Overview Title */}


      {/* Summary Cards Row */}
      {data.summary && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <SpectrumCard
            label="Habitat Health Index (HHI)"
            value={summary.habitat_health_index?.value || 0}
            status={summary.habitat_health_index?.status || 'Unknown'}
            icon={EcoIcon}
          />
          <SpectrumCard
            label="Mean Species Abundance (MSA)"
            value={summary.mean_species_abundance?.value || 0}
            status={summary.mean_species_abundance?.status || 'Unknown'}
            icon={SecurityIcon}
          />
          <SpectrumCard
            label="Human Disturbance Index (HDI)"
            value={summary.human_disturbance_index?.value || 0}
            status={summary.human_disturbance_index?.status || 'Unknown'}
            icon={GroupIcon}
          />
        </Grid>
      )}

      {/* Pillars Grid */}
      <Grid container spacing={1}>
        {data.pillars.map((pillar, index) => (
          <PillarCard
            key={index}
            pillar={pillar}
            icon={pillarIcons[index] || FoundationIcon}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default ExSituMetrics;
